import React, { useState } from "react";
import axios from "axios";
import { Toaster, toast } from "sonner";

import { SharedHeader } from "../components/SharedHeader";
import { SiteFooter } from "../components/SiteFooter";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Generic content+contact page used by /investors and /media.
 * Captures contact messages to MongoDB (collection: contact_messages).
 */
export const InfoPage = ({
  page, // "investor" | "media"
  eyebrow,
  title,
  paragraphs,
  ctaTitle,
  ctaBody,
  bullets,
}) => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      return toast.error("Please fill all fields");
    }
    try {
      setLoading(true);
      await axios.post(`${API}/contact`, { ...form, page });
      toast.success("Message sent. We'll be in touch.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Couldn't send message");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid={`${page}-page`} className="min-h-screen bg-white text-slate-900 font-sans">
      <Toaster position="top-center" richColors />
      <SharedHeader />

      <section className="mx-auto max-w-5xl px-6 lg:px-10 mt-16 sm:mt-20">
        <span
          data-testid={`${page}-eyebrow`}
          className="inline-flex items-center gap-2 rounded-full bg-[#E6FBF6] px-4 py-1.5 text-xs sm:text-sm font-medium text-[#0FB5A6]"
        >
          {eyebrow}
        </span>
        <h1
          data-testid={`${page}-title`}
          className="mt-5 text-4xl sm:text-5xl lg:text-[3.25rem] leading-[1.1] font-extrabold tracking-tight"
        >
          {title}
        </h1>
        <div className="mt-6 space-y-5 max-w-3xl text-base sm:text-lg text-slate-500 leading-relaxed">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>

        {bullets && bullets.length > 0 && (
          <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl">
            {bullets.map((b, i) => (
              <li
                key={i}
                className="rounded-2xl ring-1 ring-slate-100 bg-white p-5 shadow-[0_4px_18px_rgba(15,23,42,0.04)]"
              >
                <p className="text-sm font-semibold text-slate-900">{b.title}</p>
                <p className="mt-1 text-sm text-slate-500 leading-snug">{b.body}</p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="mx-auto max-w-5xl px-6 lg:px-10 mt-16">
        <div className="rounded-3xl ring-1 ring-[#CFE9FF] bg-gradient-to-br from-[#F4FBFF] via-white to-[#F6F2FF] p-8 sm:p-12">
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight">{ctaTitle}</h2>
          <p className="mt-3 max-w-xl text-slate-500">{ctaBody}</p>

          <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl">
            <Input
              data-testid={`${page}-name-input`}
              placeholder="Full Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              className="h-14 rounded-xl border-slate-200 bg-white px-5 text-base shadow-sm focus-visible:ring-[#22B6B0]"
            />
            <Input
              data-testid={`${page}-email-input`}
              type="email"
              placeholder="Email Address"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="h-14 rounded-xl border-slate-200 bg-white px-5 text-base shadow-sm focus-visible:ring-[#22B6B0]"
            />
            <Textarea
              data-testid={`${page}-message-input`}
              placeholder={page === "investor" ? "Tell us about your fund / interest" : "What story are you working on?"}
              value={form.message}
              onChange={(e) => setForm((f) => ({ ...f, message: e.target.value }))}
              className="sm:col-span-2 min-h-32 rounded-xl border-slate-200 bg-white px-5 py-4 text-base shadow-sm focus-visible:ring-[#22B6B0]"
            />
            <Button
              data-testid={`${page}-submit-button`}
              type="submit"
              disabled={loading}
              className="sm:col-span-2 h-14 rounded-xl text-base font-semibold text-white shadow-md transition-transform hover:scale-[1.01] disabled:opacity-70"
              style={{ background: "linear-gradient(90deg, #22B6B0 0%, #4A7AF0 55%, #7A5BFF 100%)" }}
            >
              {loading ? "Sending…" : page === "investor" ? "Request Investor Deck" : "Reach Press"}
            </Button>
          </form>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

export default InfoPage;
